import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { devWarn } from "../utils/devLogger";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft, Flame, Brain, Heart, Target, Users, Trophy, Clock,
  ChevronRight, CheckCircle2, Zap
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_CONFIG = {
  balanced: { icon: Target, color: "text-sky-500", bg: "bg-sky-100 dark:bg-sky-900/30" },
  cognitive: { icon: Brain, color: "text-indigo-500", bg: "bg-indigo-100 dark:bg-indigo-900/30" },
  consistency: { icon: Flame, color: "text-orange-500", bg: "bg-orange-100 dark:bg-orange-900/30" },
  wellness: { icon: Heart, color: "text-pink-500", bg: "bg-pink-100 dark:bg-pink-900/30" },
};

const SeasonalChallenges = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [leaderboards, setLeaderboards] = useState({});

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const res = await axios.get(`${API}/seasonal-challenges`);
        setChallenges(res.data.challenges || []);
      } catch (err) {
        if (err.response?.status !== 401) toast.error("Failed to load challenges");
      } finally {
        setLoading(false);
      }
    };
    fetchChallenges();
  }, []);

  const joinChallenge = async (challengeId) => {
    try {
      const res = await axios.post(`${API}/seasonal-challenges/${challengeId}/join`);
      toast.success(res.data.message);
      setChallenges(prev => prev.map(c =>
        c.id === challengeId ? { ...c, joined: true, total_participants: c.total_participants + 1 } : c
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join challenge");
    }
  };

  const toggleLeaderboard = async (challengeId) => {
    if (expandedId === challengeId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(challengeId);
    if (!leaderboards[challengeId]) {
      try {
        const res = await axios.get(`${API}/seasonal-challenges/${challengeId}/leaderboard`);
        setLeaderboards(prev => ({ ...prev, [challengeId]: res.data.leaderboard || [] }));
      } catch (err) {
        devWarn("Leaderboard fetch failed:", err.message);
      }
    }
  };

  const getGoalTotal = (ch) => {
    return (ch.goal_physical || 0) + (ch.goal_cognitive || 0) +
           (ch.goal_games || 0) + (ch.goal_mindfulness || 0) +
           (ch.goal_streak || 0) + (ch.goal_score || 0);
  };

  const getDaysRemaining = (endsAt) => {
    const diff = new Date(endsAt) - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900" data-testid="seasonal-challenges-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600 hover:text-sky-800 dark:text-sky-400" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t("common.dashboard")}
          </Button>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
            <span className="text-sm text-sky-700 dark:text-sky-300">Seasonal Challenges</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
              Active Challenges
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Join community challenges and earn exclusive badges
            </p>
          </div>

          {challenges.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="no-challenges">
              <Zap className="w-12 h-12 text-sky-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-sky-700 dark:text-sky-300">No active challenges right now</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Check back soon for new challenges!</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="challenges-list">
              {challenges.map((ch) => {
                const cat = CATEGORY_CONFIG[ch.category] || CATEGORY_CONFIG.balanced;
                const CatIcon = cat.icon;
                const goal = getGoalTotal(ch);
                const progressPct = goal > 0 ? Math.min(100, (ch.progress / goal) * 100) : 0;
                const daysLeft = getDaysRemaining(ch.ends_at);
                const lb = leaderboards[ch.id] || [];

                return (
                  <div key={ch.id} className="glass-card overflow-hidden" data-testid={`challenge-${ch.id}`}>
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${cat.bg}`}>
                          <CatIcon className={`w-6 h-6 ${cat.color}`} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-medium text-sky-800 dark:text-sky-200" style={{ fontFamily: "Outfit" }}>
                              {ch.title}
                            </h3>
                            {ch.completed && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Completed
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{ch.description}</p>

                          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 dark:text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {daysLeft} days left
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {ch.total_participants} participants
                            </span>
                            <span className="flex items-center gap-1">
                              <Trophy className="w-3.5 h-3.5" />
                              {ch.reward_xp} XP
                            </span>
                          </div>

                          {ch.joined && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-sky-600 dark:text-sky-400">Progress</span>
                                <span className="text-xs text-sky-600 dark:text-sky-400">{ch.progress}/{goal}</span>
                              </div>
                              <Progress value={progressPct} className="h-2 bg-sky-100 dark:bg-slate-700" />
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-4">
                            {!ch.joined && (
                              <Button
                                onClick={() => joinChallenge(ch.id)}
                                className="glow-button"
                                data-testid={`join-challenge-${ch.id}`}
                              >
                                Join Challenge
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleLeaderboard(ch.id)}
                              className="text-sky-500"
                              data-testid={`leaderboard-toggle-${ch.id}`}
                            >
                              <Trophy className="w-4 h-4 mr-1" />
                              Leaderboard
                              <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${expandedId === ch.id ? "rotate-90" : ""}`} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {expandedId === ch.id && (
                      <div className="border-t border-sky-100 dark:border-slate-700 bg-white/30 dark:bg-slate-800/30 p-4" data-testid={`leaderboard-${ch.id}`}>
                        {lb.length === 0 ? (
                          <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-2">No participants yet. Be the first!</p>
                        ) : (
                          <div className="space-y-2">
                            {lb.map((entry, i) => (
                              <div key={entry.user_id} className="flex items-center gap-3">
                                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                                  i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-100 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-sky-50 text-sky-600 dark:bg-slate-700 dark:text-sky-400"
                                }`}>
                                  {i + 1}
                                </span>
                                <p className="flex-1 text-sm text-sky-800 dark:text-sky-200">{entry.name}</p>
                                <p className="text-sm text-sky-600 dark:text-sky-400">{entry.progress} pts</p>
                                {entry.completed && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SeasonalChallenges;
